import React, { type ReactNode } from "react";

/** Parse inline Markdown elements: bold, italic, code, links */
export function renderInlineMarkdown(text: string): ReactNode {
  if (!text) return null;

  // Regex matches:
  // 1. `code`
  // 2. **bold** or __bold__
  // 3. *italic* or _italic_
  // 4. [link text](url)
  const regex = /(`[^`]+`|\*\*[^*]+\*\*|__[^_]+__|\*[^*]+\*|_[^_]+_|\[[^\]]+\]\([^)]+\))/g;
  const parts = text.split(regex);

  return parts.map((part, i) => {
    if (!part) return null;

    if (part.startsWith("`") && part.endsWith("`") && part.length >= 2) {
      return (
        <code key={i} className="t1s-md-code">
          {part.slice(1, -1)}
        </code>
      );
    }

    if (
      (part.startsWith("**") && part.endsWith("**") && part.length >= 4) ||
      (part.startsWith("__") && part.endsWith("__") && part.length >= 4)
    ) {
      return (
        <strong key={i} style={{ fontWeight: 700, color: "inherit" }}>
          {renderInlineMarkdown(part.slice(2, -2))}
        </strong>
      );
    }

    if (
      (part.startsWith("*") && part.endsWith("*") && part.length >= 2) ||
      (part.startsWith("_") && part.endsWith("_") && part.length >= 2)
    ) {
      return <em key={i}>{renderInlineMarkdown(part.slice(1, -1))}</em>;
    }

    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    // Only web/mail links become <a> — this renders recruiter text on public job pages too, so a
    // `[x](javascript:…)` or `data:` link is shown as plain text, never made clickable.
    if (linkMatch && !/^(https?:|mailto:|\/(?!\/)|#)/i.test((linkMatch[2] ?? "").trim())) {
      return linkMatch[1];
    }
    if (linkMatch) {
      return (
        <a
          key={i}
          href={linkMatch[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="t1s-md-link"
        >
          {linkMatch[1]}
        </a>
      );
    }

    return part;
  });
}

/** Render multiline Markdown blocks: headings, bullet lists, numbered lists, paragraphs */
export function MarkdownView({
  content,
  className,
  style,
}: {
  content: string;
  className?: string;
  style?: React.CSSProperties;
}): ReactNode {
  if (!content) return null;

  const lines = content.split("\n");
  const elements: ReactNode[] = [];

  let currentList: { type: "ul" | "ol"; items: string[] } | null = null;

  const flushList = (keyPrefix: number) => {
    if (!currentList) return;
    if (currentList.type === "ul") {
      elements.push(
        <ul
          key={`ul-${keyPrefix}`}
          // explicit markers — a global list reset strips them otherwise
          style={{ margin: "6px 0 8px", paddingLeft: 22, display: "grid", gap: 4, listStyle: "disc outside" }}
        >
          {currentList.items.map((item, idx) => (
            <li key={idx} style={{ lineHeight: 1.55, display: "list-item" }}>
              {renderInlineMarkdown(item)}
            </li>
          ))}
        </ul>
      );
    } else {
      elements.push(
        <ol
          key={`ol-${keyPrefix}`}
          style={{ margin: "6px 0 8px", paddingLeft: 22, display: "grid", gap: 4, listStyle: "decimal outside" }}
        >
          {currentList.items.map((item, idx) => (
            <li key={idx} style={{ lineHeight: 1.55, display: "list-item" }}>
              {renderInlineMarkdown(item)}
            </li>
          ))}
        </ol>
      );
    }
    currentList = null;
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    // Check headings
    if (trimmed.startsWith("### ")) {
      flushList(index);
      elements.push(
        <h4
          key={`h4-${index}`}
          style={{ margin: "10px 0 4px", fontSize: 14, fontWeight: 700, color: "inherit" }}
        >
          {renderInlineMarkdown(trimmed.replace(/^###\s+/, ""))}
        </h4>
      );
      return;
    }
    if (trimmed.startsWith("## ")) {
      flushList(index);
      elements.push(
        <h3
          key={`h3-${index}`}
          style={{ margin: "12px 0 6px", fontSize: 15, fontWeight: 700, color: "inherit" }}
        >
          {renderInlineMarkdown(trimmed.replace(/^##\s+/, ""))}
        </h3>
      );
      return;
    }
    if (trimmed.startsWith("# ")) {
      flushList(index);
      elements.push(
        <h2
          key={`h2-${index}`}
          style={{ margin: "14px 0 6px", fontSize: 16, fontWeight: 700, color: "inherit" }}
        >
          {renderInlineMarkdown(trimmed.replace(/^#\s+/, ""))}
        </h2>
      );
      return;
    }

    // Check bullet list item (* or - or •)
    const bulletMatch = trimmed.match(/^[-*•]\s+(.*)$/);
    if (bulletMatch && bulletMatch[1]) {
      if (!currentList || currentList.type !== "ul") {
        flushList(index);
        currentList = { type: "ul", items: [] };
      }
      currentList.items.push(bulletMatch[1]);
      return;
    }

    // Check numbered list item (1. 2. etc)
    const numberedMatch = trimmed.match(/^\d+\.\s+(.*)$/);
    if (numberedMatch && numberedMatch[1]) {
      if (!currentList || currentList.type !== "ol") {
        flushList(index);
        currentList = { type: "ol", items: [] };
      }
      currentList.items.push(numberedMatch[1]);
      return;
    }

    // Empty line / paragraph break
    if (!trimmed) {
      flushList(index);
      return;
    }

    // Regular paragraph line
    flushList(index);
    elements.push(
      <p key={`p-${index}`} style={{ margin: "0 0 6px", lineHeight: 1.55 }}>
        {renderInlineMarkdown(trimmed)}
      </p>
    );
  });

  flushList(lines.length);

  return (
    <div className={className} style={style}>
      {elements}
    </div>
  );
}
